import { faArrowRight, faThumbsUp, faTimes, faPencil } from '@fortawesome/pro-solid-svg-icons';
import { ActionButton, HeadingItem } from 'components';
import { ConfirmationPopup, SignaturePopup, StampedDeliveryReceiptPopup } from 'components/pop-up';
import { POCard, RecapPallet, RecapSignature, RecapSummary, ReturnItemCard, VerifyCompanyEmailCard } from 'components/recap';
import { TripContext } from 'contexts';
import notify from 'devextreme/ui/notify';
import { ItemVerificationView } from 'enums';
import React, { Fragment, useContext, useRef, useState, useLayoutEffect, useEffect } from 'react';
import { useImageUploadService, useLoadManagerService, useNavigation, useParamService, useReturnItemService, useSettingService, useStopService, useTripService } from 'services';
import { FailedRequestType, RecapField, StopStatus, Temperature, TripStatus } from 'services/enums';
import { SyncingStatus } from 'services/enums/syncingStatus';
import { Company, CustomerPo, OrderItemChangeImage, LineItem, Order, OrderItem, ReturnItem, Signature, Stop } from 'services/types';
import SignaturePad from 'signature_pad';
import { CheckBoxItem } from 'types';
import './recap.scss';
import { distinct } from 'helpers/arrayHelper';
import _ from 'lodash'
import {appInsights} from "../../utils/appInsights";
import { envConfig } from "config"

export const RecapPage = () => {

    const { getIntValue, getStringValue } = useParamService
    const { navigateTo, navigateBack } = useNavigation()
    const { updateStopStatus } = useStopService()
    const { updateTripStatus } = useTripService()
    const { completeStop } = useLoadManagerService()
    const { sendImagesToOnBase } = useImageUploadService()
    const { getTrainingMode } = useSettingService()
    const { isReturnItemCompleted } = useReturnItemService()
    const config = envConfig();    

    const tripContext = useContext(TripContext)

    const featureFlagCollectEmails: boolean = `${config.FEATURE_FLAG_COLLECT_EMAILS}` === 'true'

    const tripId: number | undefined = getIntValue('tripId')
    const stopId: number | undefined = getIntValue('stopId')
    const readOnly: boolean | undefined = getStringValue('readOnly') == 'true'
    const isTrainingMode: boolean = getTrainingMode()
    const userAgent = navigator.userAgent;
    const signatureDraftLabel = `receiver signature stop:${stopId}`;
    const toEditEmailPage = useRef(false);
    const hasRestoredRef = useRef(false);
    const [isSignatureDraftLoaded, setSignatureDraftLoaded] = useState(false);

    const pos: CustomerPo[] = []
    const orders: Order[] = tripContext.orders.filter(lp => lp.stopId === stopId) ?? []
    const stopItems: OrderItem[] = tripContext.orderItems.filter(item => orders.some(order => order.id === item.orderId))
    const stop: Stop = tripContext.stops.find(stop => stop.id == stopId) ?? {} as Stop
    const company: Company = tripContext.companies.find(com => com.id == stop.companyId) ?? {} as Company
    const appointments: Stop[] = tripContext.stops.filter(stop => stop.tripId == tripId) ?? []
    const returnItems: ReturnItem[] = tripContext.returnItems.filter(returnItem => orders.some(order => order.id === returnItem.orderId))
    const lineItems: LineItem[] = tripContext.lineItems.filter(lineItem =>
        stopItems.some(orderItem => lineItem.id === orderItem.lineItemId) ||
        returnItems.some(returnItem => lineItem.id === returnItem.lineItemId)
    ) ?? []

    const [temperatures] = useState<CheckBoxItem[]>([
        { id: Temperature.Dry, isSelected: (getStringValue('dry') == 'true'), text: 'Dry', isEnable: true },
        { id: Temperature.Frozen, isSelected: (getStringValue('frozen') == 'true'), text: 'Frozen', isEnable: true },
        { id: Temperature.Refrigerated, isSelected: (getStringValue('ref') == 'true'), text: 'Ref', isEnable: true }
    ])

    const orderItemChangeImages: OrderItemChangeImage[] = tripContext.orderItemChangeImages.filter(x => stopItems.some(si => si.id == x.orderItemId))

    let signature: Signature = { acceptAll: false, isSigned: false, receiverName: '', signatureImage: '' } as Signature
    if (readOnly) {
        if (temperatures.filter(temp => temp.isSelected).length > 1) {
            signature = tripContext.signatures.find(sign => orders.some(order => order.signatureId === sign.id)) ?? { acceptAll: false, isSigned: false, receiverName: '', signatureImage: '' } as Signature
        } else {
            const orderItems: OrderItem[] = stopItems.filter(oi => lineItems.some(li => li.id === oi.lineItemId && li.temperature === temperatures.find(temp => temp.isSelected)?.id))
            signature = tripContext.signatures.find(sign => orders.some(order => (orderItems.some(oi => oi.orderId === order.id) && order.signatureId === sign.id) || (returnItems.some(ri => ri.orderId === order.id) && order.signatureId === sign.id))) ?? { acceptAll: false, isSigned: false, receiverName: '', signatureImage: '' } as Signature
        }
    }

    stopItems.forEach(item => {
        if (item.customerPoNumber && !pos.some(po => po.id === item.customerPoNumber)) {
            pos.push({ id: item.customerPoNumber })
        }
    })

    const signaturePadRef = useRef<SignaturePad | null>(null);
    const [signatureField, setSignatureField] = useState<Signature>(signature)
    const [isConfirmationPopupVisible, setIsConfirmationPopupVisible] = useState<boolean>(false)
    const [isSignaturePopupVisible, setIsSignaturePopupVisible] = useState<boolean>(false)
    const [isStampedDeliveryReceiptVisible, setIsStampedDeliveryReceiptVisible] = useState<boolean>(false) // Setting up isStampedDeliveryReceiptVisible state with it setter

    const stopItemsByTempSelection: OrderItem[] = stopItems.filter(orderItem => temperatures.some(temp => temp.id === lineItems.find(lineItem => lineItem.id == orderItem.lineItemId)?.temperature && temp.isSelected))
    const returnItemsByTempSelection: ReturnItem[] = returnItems.filter(item => temperatures.some(temp => temp.id === lineItems.find(lineItem => lineItem.id == item.lineItemId)?.temperature && temp.isSelected))

    const onContentReadySignature = () => {
        var canvas = document.getElementById('signature-pad') as HTMLCanvasElement;
        signaturePadRef.current = new SignaturePad(canvas);
    }

    const onClickClearSignature = () => {
        const signaturePad = signaturePadRef.current;
        if (!signaturePad) return;

        signaturePad.clear();
        setSignatureField(prev => ({ ...prev, signatureImage: '', isSigned: false }));
    }

    const onClickOkSignature = () => {
        if (!isSignatureDraftLoaded) return;

        const signaturePad = signaturePadRef.current;
        if (!signaturePad) return;

        if (signaturePad.isEmpty()) {notify(`Please provide a signature first`, 'info', 5000); return; }

        var data: string = signaturePad.toDataURL('image/png');
        setSignatureField({ ...signatureField, signatureImage: data, isSigned: true });

        try { sessionStorage.setItem(signatureDraftLabel, JSON.stringify({ ...signatureField, signatureImage: data, isSigned: true })); } catch { }

        setIsSignaturePopupVisible(false);
    }

    const onBeforeEditEmail = () => {
        toEditEmailPage.current = true;
        if (!isSignatureDraftLoaded) return;
        try { sessionStorage.setItem(signatureDraftLabel, JSON.stringify(signatureField)) } catch { }
    }

    const debouncedOnClickYes = _.debounce(() => onClickYes(), 500);

    const onClickYes = async () => {
        let ordersByTempSelection: Order[] = orders.filter(order => stopItemsByTempSelection.some(item => item.orderId == order.id)
        || returnItemsByTempSelection.some(item => item.orderId == order.id))

        let temperatures: string[] = ordersByTempSelection.map(x => x.temperature).filter(distinct)

        let signature: Signature = {
            id: new Date().getTime(),
            isSigned: signatureField.isSigned,
            receiverName: signatureField.receiverName,
            signatureImage: signatureField.signatureImage,
            signedTime: new Date(),
            stopId: stopId!,
            temperatures: temperatures
        }

        ordersByTempSelection.forEach(order => {
            order.signatureId = signature.id
        });

        tripContext.orders.bulkPut(ordersByTempSelection)
        tripContext.signatures.put(signature)
        try { sessionStorage.removeItem(signatureDraftLabel) } catch { }
        tripContext.returnItems.bulkPut(returnItemsByTempSelection)

        setIsConfirmationPopupVisible(false);

        let pendingStops: Stop[] = appointments.filter(stop => stop.stopStatus != StopStatus.Complete)
        const notSignedItems: Order[] = orders ? orders.filter(order => order.signatureId === null) : [];
        const notVerifiedItems: OrderItem[] = stopItems ? stopItems.filter(items => items.verifiedMethod === null) : [];
        const notVerifiedReturnItems: ReturnItem[] = returnItems ? returnItems.filter(item => !isReturnItemCompleted(item)) : [];
        
        if (notVerifiedItems.length > 0 || notSignedItems.length > 0 || notVerifiedReturnItems.length > 0) {
            appInsights.trackEvent({name: 'Complete Stop button clicked but going to navigate back [notVerifiedItems.length > 0]', properties: {
                stopId: stopId, 
                userAgent: userAgent, 
                notVerifiedItems: notVerifiedItems, 
                notSignedItems: notSignedItems, 
                notVerifiedReturnItems: notVerifiedReturnItems}})

            navigateBack()
        }
        else if (pendingStops.length > 1) {
            appInsights.trackEvent({name: 'Complete Stop button clicked but going to navigate back [pendingStops.length > 1]', properties: {
                stopId: stopId, 
                userAgent: userAgent, 
                pendingStops: pendingStops}})

            completeCurrentStop(stopId!)
            navigateBack('/load-appointments')
        }
        else if (pendingStops.length == 1) { // last stop for this trip
            appInsights.trackEvent({name: 'Complete Stop button clicked but going to update trip status', properties: {
                stopId: stopId, 
                userAgent: userAgent, 
                pendingStops: pendingStops}})

            updateTripStatus(tripId!, TripStatus.Complete)
            completeCurrentStop(stopId!)
            navigateBack('/load-assignments')
        }
        else { // this condition occurs only if the dexie is cleared for some reason
            appInsights.trackEvent({name: 'Some error occured, please retry later', properties: {
                stopId: stopId, 
                userAgent: userAgent}})

            notify(`Some error occured, please retry later`, 'info', 5000)
        }
    }

    const completeCurrentStop = async (stopId: number) => {
        appInsights.trackEvent({name: 'Updating stop status', properties: {stopId: stopId}})
       
        if (!isTrainingMode) {
            appInsights.trackEvent({name: 'Syncing trip context', properties: {stopId: stopId}})
            tripContext.syncStatus.put({id: stopId!, data: null, status: SyncingStatus.Syncing, type: FailedRequestType.StopCompletion})
            orderItemChangeImages && orderItemChangeImages.length > 0 && tripContext.syncStatus.put({id: parseFloat(`${orderItemChangeImages[0].loadNumber}${orderItemChangeImages[0].orderNumber}${orderItemChangeImages[0].itemNumber}`), data: null, status: SyncingStatus.Syncing, type: FailedRequestType.ImageUpload})
           
            completeStop(stop.id)
            appInsights.trackEvent({name: 'Sending order item change images to OnBase', properties: {stopId: stopId}})
            sendImagesToOnBase(orderItemChangeImages)
            updateStopStatus(stopId!, StopStatus.Complete)
        }
    }

    useLayoutEffect(() => {
        if (hasRestoredRef.current) return;
        try {
          const raw = sessionStorage.getItem(signatureDraftLabel)
          if (raw) setSignatureField(JSON.parse(raw))
        } catch { }
        hasRestoredRef.current = true;
        setSignatureDraftLoaded(true)
    }, [signatureDraftLabel])

    useEffect(() => {
        return () => {
          if (!toEditEmailPage.current) {
            try { sessionStorage.removeItem(signatureDraftLabel); } catch { }
          }
        }
    }, [signatureDraftLabel])

    useEffect(() => {
        if (toEditEmailPage.current) {
          toEditEmailPage.current = false;
        }
    }, [])

    const onValueChanged = (field: RecapField, e: any) => {
        if (!isSignatureDraftLoaded) return;
        let next = signatureField;
        if (field === RecapField.Accept) next = { ...next, acceptAll: e.value };
        else if (field === RecapField.Name) next = { ...next, receiverName: e.value };
        else return;

        setSignatureField(next);
        try { sessionStorage.setItem(signatureDraftLabel, JSON.stringify(next)); } catch { }
    }

    return (
        <Fragment>
            <div style={{ paddingBottom: "30px" }}>
                <div className={'recap-heading'}>
                    <HeadingItem text={company.name!} />
                </div>
                <div className={'recap-details'}>
                    <div className={'card-material'}>
                        <RecapPallet stopId={stopId} />
                        <RecapSummary items={stopItemsByTempSelection} />
                    </div>
                    <Fragment>
                        {pos.map(po =>
                            <POCard
                                key={po.id}
                                customerPo={po.id}
                                items={stopItemsByTempSelection.filter(items => items.customerPoNumber == po.id)}
                                onClick={() => navigateTo(`item-verification?stopId=${stopId}&searchId=${ItemVerificationView.PoDetails}&poNumber=${po.id}&dry=${temperatures[0].isSelected}&frozen=${temperatures[1].isSelected}&ref=${temperatures[2].isSelected}`, 'PO Details')}
                            >
                            </POCard>)}
                    </Fragment>
                    <Fragment>
                        {returnItemsByTempSelection.map(item => <ReturnItemCard key={item.id} returnItemId={item.id} />)}
                    </Fragment>
                    {featureFlagCollectEmails &&
                        <div>
                            <VerifyCompanyEmailCard 
                                isEditEmailPage={false}
                                emailHeaderText={'Verify E-mail Address for Pre and Post Delivery Receipts'}
                                stopId={stopId!}
                                onBeforeEditEmail={onBeforeEditEmail}
                            /> 
                        </div>
                    }
                    {(stopItemsByTempSelection.length > 0 || returnItemsByTempSelection.length > 0) &&
                        <div className={'card-material'} >
                            <RecapSignature
                                isSignaturePopupVisible={isSignaturePopupVisible}
                                isRecapReadOnly={readOnly}
                                signatureField={signatureField}
                                onValueChanged={onValueChanged}
                                onClickTapToSign={() => !readOnly && setIsSignaturePopupVisible(true)}
                            />
                        </div>
                    }
                </div>
            </div>
            {!readOnly &&
                <div className={'bottom-fix-action-row'}>
                    <ActionButton
                        enabled={signatureField.acceptAll && signatureField.receiverName != ""}
                        icon={faArrowRight}
                        buttonColor={'#76AB21'}
                        onClick={() => {
                            if(company.isStampedDrRequired)
                            {
                                setIsStampedDeliveryReceiptVisible(true);
                            }
                            else {
                                setIsStampedDeliveryReceiptVisible(false); 
                            }
                            setIsConfirmationPopupVisible(true)}
                        }
                        buttonText={'Complete'}
                    />
                </div>
            }
            <ConfirmationPopup
                isVisible={isConfirmationPopupVisible}
                title="Confirmation"
                message={signatureField.isSigned ?
                    `Are you sure you want to continue? No further changes can be made to these items.`
                    :
                    "Are you sure you want to continue without a signature? No further changes can be made to these items."}
                button1={{ icon: faTimes }}
                button2={{ icon: faThumbsUp }}
                onClickButton1={() => setIsConfirmationPopupVisible(false)}
                onClickButton2={debouncedOnClickYes}
                onClickCloseButton={() => setIsConfirmationPopupVisible(false)}
            />
            <SignaturePopup
                isVisible={isSignaturePopupVisible}
                heading={'Receiver Signature'}
                onClickOk={() => onClickOkSignature()}
                onClickClear={onClickClearSignature}
                onClickClose={() => setIsSignaturePopupVisible(false)}
                onContentReady={onContentReadySignature}
            >
            </SignaturePopup>
            <StampedDeliveryReceiptPopup
                isVisible={isStampedDeliveryReceiptVisible}
                title="Stamped Delivery Receipt Required"
                message="Please ensure we receive a second delivery receipt that is stamped by this customer."
                button={{ icon: faThumbsUp }}
                onClickButton={() =>setIsStampedDeliveryReceiptVisible(false)}
            />
        </Fragment>
    )
};
